import { useMemo } from "react";
import { Pause, Play, Mic, Keyboard, AudioLines, Loader2, Sparkles, RotateCw } from "lucide-react";
import { fmtTime } from "@/lib/langs";

const KIND = { voice: Mic, text: Keyboard, dictation: AudioLines };

const playIcon = (state, on) => {
  if (state === "loading") return <Loader2 size={11} className="animate-spin" />;
  return on ? <Pause size={11} /> : <Play size={11} />;
};

const borderFor = (system, mine) => {
  if (system) return "border-[color:var(--au-violet)]";
  return mine ? "border-[color:var(--au-green)]" : "border-[color:var(--au-cyan)]";
};

const speakerLabel = (m, system, mine) => {
  if (system) return "AURORA";
  return mine ? "YOU" : m.user_name?.toUpperCase();
};

const latencyLabel = (m) => [m.stt_ms && `STT ${m.stt_ms}ms`, m.translate_ms && `TR ${m.translate_ms}ms`].filter(Boolean).join(" · ");

const PlayButton = ({ m, lang, audioState, playing, onPlay }) => {
  const key = `${m.message_id}:${lang}`;
  const state = audioState[key];
  const on = playing === key && state !== "loading";
  return (
    <button onClick={() => onPlay(lang)} disabled={state === "loading"} data-testid={`play-${m.message_id}-${lang}`} aria-label={`Listen in ${lang}`}
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center border border-border transition-colors duration-200 hover:border-[color:var(--au-cyan)] hover:text-[color:var(--au-cyan)] disabled:opacity-50">
      {playIcon(state, on)}
    </button>
  );
};

const Pending = ({ m, lang, failed, onRetry }) => {
  const state = failed[`${m.message_id}:${lang}`];
  if (!state) return <span className="caret font-mono text-xs">translating</span>;
  return (
    <button onClick={() => onRetry(lang)} disabled={state === "retrying"} data-testid={`retry-${m.message_id}-${lang}`}
      className="inline-flex items-center gap-1.5 font-mono text-xs text-[color:var(--au-red)] transition-opacity duration-200 hover:opacity-80 disabled:opacity-50">
      <RotateCw size={11} className={state === "retrying" ? "animate-spin" : ""} /> {state === "retrying" ? "retrying" : "translation failed · retry"}
    </button>
  );
};

const Translations = ({ m, langs, myLang, failed, onRetry, ...audio }) => {
  const list = useMemo(() => langs.filter((l) => l !== m.source_language), [langs, m.source_language]);
  if (!list.length) return null;
  return (
    <ul className="mt-2 space-y-1.5" data-testid="message-translations">
      {list.map((l) => (
        <li key={l} className={`flex items-start gap-2 text-sm ${l === myLang ? "text-foreground" : "text-muted-foreground"}`} data-testid={`message-translation-${l}`}>
          <PlayButton m={m} lang={l} {...audio} />
          <span className={`mt-0.5 font-mono text-[10px] ${l === myLang ? "text-[color:var(--au-cyan)]" : ""}`}>[{l.toUpperCase()}]</span>
          <span className="leading-snug">{m.translations?.[l] ?? <Pending m={m} lang={l} failed={failed} onRetry={onRetry} />}</span>
        </li>
      ))}
    </ul>
  );
};

export const MessageCard = ({ m, mine, myLang, langs, failed, onRetry, ...audio }) => {
  const system = m.kind === "greeting" || m.kind === "system";
  const Icon = system ? Sparkles : KIND[m.kind] || Keyboard;
  const border = borderFor(system, mine);
  const latency = useMemo(() => latencyLabel(m), [m]);
  return (
    <div className={`fade-up border-l-2 py-1 pl-4 ${border}`} data-testid={system ? `greeting-${m.message_id}` : `message-${m.message_id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] tracking-widest text-muted-foreground">
        <span className={`font-semibold ${system ? "aurora-text" : "text-foreground"}`}>{speakerLabel(m, system, mine)}</span>
        <span>[{m.source_language.toUpperCase()}]</span>
        <Icon size={12} />
        <span>{fmtTime(m.created_at)}</span>
        {!system && <span className="text-[color:var(--au-green)]" title="voice-to-text + slowest translation" data-testid="message-latency">{latency}</span>}
      </div>
      <div className="mt-2 flex items-start gap-2">
        <PlayButton m={m} lang={m.source_language} {...audio} />
        <p className={`font-display font-semibold leading-snug ${system ? "text-base" : "text-lg sm:text-xl"}`} data-testid={system ? "greeting-text" : "message-original-text"}>{m.original_text}</p>
      </div>
      <Translations m={m} langs={langs} myLang={myLang} failed={failed} onRetry={onRetry} {...audio} />
    </div>
  );
};
